import { useState, useEffect, useCallback } from 'react';
import { demoProducts, demoScenes, demoResults } from '@/mocks/demo';
import { Logo } from '@/components/feature/Logo';

export default function DemoPage() {
  const [step, setStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedScene, setSelectedScene] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [dots, setDots] = useState('');
  const [activeTab, setActiveTab] = useState<'before' | 'after'>('after');

  const selectedProductData = demoProducts.find((p) => p.id === selectedProduct);
  const selectedSceneData = demoScenes.find((s) => s.id === selectedScene);

  useEffect(() => {
    if (isGenerating) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isGenerating]);

  const handleGenerate = useCallback(() => {
    if (!selectedProduct || !selectedScene) return;
    setIsGenerating(true);
    setShowResult(false);
    setTimeout(() => {
      setIsGenerating(false);
      setShowResult(true);
      setStep(4);
    }, 3000);
  }, [selectedProduct, selectedScene]);

  const handleReset = () => {
    setStep(1);
    setSelectedProduct(null);
    setSelectedScene(null);
    setIsGenerating(false);
    setShowResult(false);
    setActiveTab('after');
  };

  const resultImage =
    selectedProduct && selectedScene
      ? demoResults[selectedProduct]?.[selectedScene]
      : null;

  return (
    <div className="min-h-screen bg-surface">
      {/* Simple Navbar for Demo */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-surface-dark/30">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center">
              <Logo height={56} />
            </a>
            <a href="/" className="text-sm font-medium text-dark-muted hover:text-dark transition-colors">
              Back to Home
            </a>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-white border-b border-surface-dark/30">
        <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-light text-accent text-xs font-semibold tracking-wider uppercase mb-6">
              <i className="ri-magic-line" />
              Live Demo
            </span>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark leading-[1.1] tracking-tight mb-4">
              Try the AI Magic
            </h1>
            <p className="text-dark-muted text-base md:text-lg leading-relaxed">
              Pick a product and a scene. Watch AI transform a plain photo into a stunning lifestyle shot in seconds.
            </p>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="w-full px-6 md:px-10 lg:px-16 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Pick Product' },
              { num: 2, label: 'Pick Scene' },
              { num: 3, label: 'Generate' },
              { num: 4, label: 'Result' },
            ].map((s, i) => (
              <div key={s.num} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      step >= s.num
                        ? 'bg-primary text-white'
                        : 'bg-surface-dark text-dark-muted'
                    }`}
                  >
                    {step > s.num ? (
                      <i className="ri-check-line" />
                    ) : (
                      s.num
                    )}
                  </div>
                  <span className={`text-xs mt-2 font-medium hidden sm:block ${step >= s.num ? 'text-dark' : 'text-dark-muted'}`}>
                    {s.label}
                  </span>
                </div>
                {i < 3 && (
                  <div className={`flex-1 h-px mx-2 sm:mx-4 ${step > s.num ? 'bg-primary' : 'bg-surface-dark'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-6 md:px-10 lg:px-16 pb-20">
        <div className="max-w-5xl mx-auto">
          {/* STEP 1: Product Selector */}
          {(step === 1 || step === 2 || step === 3) && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-bold text-dark">1. Choose a Product</h2>
                <span className="text-sm text-dark-muted">{demoProducts.length} available</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {demoProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => {
                      setSelectedProduct(product.id);
                      setShowResult(false);
                      if (step === 1) setStep(2);
                    }}
                    className={`group relative rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                      selectedProduct === product.id
                        ? 'border-primary shadow-lg shadow-primary/15'
                        : 'border-transparent hover:border-surface-dark/50 bg-white'
                    }`}
                  >
                    <div className="aspect-square">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3 text-left">
                      <p className="text-sm font-semibold text-dark truncate">{product.name}</p>
                      <p className="text-xs text-dark-muted">{product.category}</p>
                    </div>
                    {selectedProduct === product.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Scene Selector */}
          {(step >= 2 && step <= 3) && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-bold text-dark">2. Choose a Scene</h2>
                <span className="text-sm text-dark-muted">{demoScenes.length} scenes</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {demoScenes.map((scene) => (
                  <button
                    key={scene.id}
                    onClick={() => {
                      setSelectedScene(scene.id);
                      setShowResult(false);
                      if (step === 2) setStep(3);
                    }}
                    className={`group relative rounded-2xl overflow-hidden border-2 transition-all duration-300 text-left ${
                      selectedScene === scene.id
                        ? 'border-accent shadow-lg shadow-accent/15'
                        : 'border-transparent hover:border-surface-dark/50 bg-white'
                    }`}
                  >
                    <div className="aspect-[3/2]">
                      <img
                        src={scene.image}
                        alt={scene.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-sm font-bold text-white">{scene.name}</p>
                        <p className="text-xs text-white/70">{scene.description}</p>
                      </div>
                    </div>
                    {selectedScene === scene.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: Generate Button */}
          {step === 3 && selectedProduct && selectedScene && (
            <div className="text-center py-8">
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-surface-dark/30">
                <div className="flex items-center justify-center gap-6 mb-8">
                  {/* Selected Product Preview */}
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-xl overflow-hidden mx-auto mb-2 border-2 border-primary">
                      <img
                        src={selectedProductData?.image}
                        alt={selectedProductData?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-xs font-semibold text-dark">{selectedProductData?.name}</p>
                  </div>

                  <i className="ri-arrow-right-line text-2xl text-primary animate-pulse" />

                  {/* Selected Scene Preview */}
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-xl overflow-hidden mx-auto mb-2 border-2 border-accent">
                      <img
                        src={selectedSceneData?.image}
                        alt={selectedSceneData?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-xs font-semibold text-dark">{selectedSceneData?.name}</p>
                  </div>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="inline-flex items-center gap-3 px-10 py-4 bg-primary hover:bg-primary-hover disabled:bg-primary/60 text-white text-lg font-bold rounded-full transition-all shadow-lg shadow-primary/20"
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-bard-line animate-spin" />
                      Generating{dots}
                    </>
                  ) : (
                    <>
                      <i className="ri-magic-line" />
                      Generate Marketing Shot
                    </>
                  )}
                </button>
                <p className="text-sm text-dark-muted mt-4">Takes about 3 seconds</p>
              </div>
            </div>
          )}

          {/* GENERATING STATE */}
          {isGenerating && (
            <div className="text-center py-16">
              <div className="relative w-64 h-64 mx-auto mb-8">
                {/* Orbiting particles */}
                <div className="absolute inset-0">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary animate-bounce" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-accent animate-bounce" style={{ animationDelay: '0.2s' }} />
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0.4s' }} />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-accent/60 animate-bounce" style={{ animationDelay: '0.6s' }} />
                </div>

                {/* Center processing image */}
                <div className="absolute inset-8 rounded-2xl overflow-hidden border-2 border-primary/30">
                  <img
                    src={selectedProductData?.image}
                    alt="Processing"
                    className="w-full h-full object-cover opacity-50 blur-sm"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-3">
                      <i className="ri-bard-line text-white text-2xl animate-spin" />
                    </div>
                    <span className="text-dark font-semibold text-sm">AI Working{dots}</span>
                  </div>
                </div>
              </div>

              <div className="max-w-md mx-auto">
                {[
                  'Analyzing product shape and lighting...',
                  'Generating matching scene context...',
                  'Blending product into lifestyle environment...',
                  'Applying final color grade and polish...',
                ].map((text, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 py-2 transition-all duration-500 ${
                      i < Math.floor((dots.length + 1) / 1)
                        ? 'opacity-100'
                        : 'opacity-30'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                        i < Math.floor((dots.length + 1) / 1)
                          ? 'bg-primary'
                          : 'bg-surface-dark'
                      }`}
                    >
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                    <span className="text-sm text-dark">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: RESULT */}
          {showResult && resultImage && (
            <div className="py-4">
              <div className="bg-white rounded-2xl border border-surface-dark/30 overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-surface-dark/30">
                  <button
                    onClick={() => setActiveTab('before')}
                    className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                      activeTab === 'before'
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-dark-muted hover:text-dark'
                    }`}
                  >
                    <i className="ri-image-line mr-1" />
                    Before
                  </button>
                  <button
                    onClick={() => setActiveTab('after')}
                    className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                      activeTab === 'after'
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-dark-muted hover:text-dark'
                    }`}
                  >
                    <i className="ri-sparkling-line mr-1" />
                    AI Result
                  </button>
                </div>

                {/* Image Display */}
                <div className="p-6 md:p-10">
                  <div className="max-w-lg mx-auto">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
                      <img
                        src={activeTab === 'before' ? selectedProductData?.image : resultImage}
                        alt={activeTab === 'before' ? 'Before' : 'AI Result'}
                        className="w-full aspect-square object-cover"
                      />

                      {activeTab === 'after' && (
                        <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-primary text-white text-xs font-bold flex items-center gap-1">
                          <i className="ri-sparkling-line" />
                          AI Generated
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="max-w-lg mx-auto mt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-3 py-1 rounded-full bg-primary-light text-primary text-xs font-semibold">
                        {selectedProductData?.name}
                      </span>
                      <i className="ri-arrow-right-line text-dark-muted text-xs" />
                      <span className="px-3 py-1 rounded-full bg-accent-light text-accent text-xs font-semibold">
                        {selectedSceneData?.name}
                      </span>
                    </div>

                    <p className="text-sm text-dark-muted leading-relaxed mb-6">
                      AI placed your {selectedProductData?.name.toLowerCase()} into a{' '}
                      <strong className="text-dark">{selectedSceneData?.name}</strong>{' '}
                      scene. The lighting, shadows, and reflections are all automatically matched to look natural and professional.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <a
                        href={resultImage}
                        download
                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white text-sm font-bold rounded-full transition-all shadow-md shadow-primary/15"
                      >
                        <i className="ri-download-line" />
                        Download Image
                      </a>
                      <button
                        onClick={handleReset}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-dark/15 hover:border-primary/40 text-dark text-sm font-bold rounded-full transition-all"
                      >
                        <i className="ri-refresh-line" />
                        Try Another
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}